import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { FiX } from 'react-icons/fi';

import ActivityIndicator from '../../../components/ActivityIndicator';
import BRLMoneyFormat from '../../../utils/BRLMoneyFormat';
import { ModalHandles } from '../Modal';
import {
  AtacPizzaSizeDetails,
  formatPizzaQuantity,
  formatPizzaSize,
  normalizePizzaKey,
  PizzaItemStat,
  PizzaSort,
  PizzaSortKey,
  PizzaSummary,
  resolvePizzaSizeDetails,
  sortPizzaStats,
  togglePizzaSort,
} from '../dashboardDetailsPizzas';
import {
  Dialog,
  Empty,
  ErrorText,
  Footer,
  LoadingContainer,
  Section,
  Share,
  SizeTab,
  Sizes,
  Table,
  Tables,
} from './styles';

interface PizzaDetailsModalProps {
  summary: PizzaSummary;
  atacDetails: Record<string, AtacPizzaSizeDetails>;
  detailsVersion: number;
  onSelectSize: (size: string) => void;
}

export interface PizzaDetailsModalHandles {
  open(): void;
  close(): void;
}

const DEFAULT_SORT: PizzaSort = { key: 'quantity', direction: 'desc' };

const COLUMNS: Array<{ key: PizzaSortKey; label: string; numeric?: boolean }> = [
  { key: 'name', label: 'Descrição' },
  { key: 'quantity', label: 'Quantidade', numeric: true },
  { key: 'total', label: 'Total', numeric: true },
];

function formatMoney(value: number): string {
  return value.toLocaleString('pt-BR', BRLMoneyFormat);
}

interface StatTableProps {
  label: string;
  items: PizzaItemStat[];
  sort: PizzaSort;
  emptyLabel: string;
  onSort: (key: PizzaSortKey) => void;
}

const StatTable: React.FC<StatTableProps> = ({
  label,
  items,
  sort,
  emptyLabel,
  onSort,
}) => {
  const sorted = sortPizzaStats(items, sort.key, sort.direction);
  const quantityTotal = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Section aria-label={label}>
      <h4>{label}</h4>
      {sorted.length === 0 ? (
        <Empty>{emptyLabel}</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className={column.numeric ? 'numeric' : undefined}
                  aria-sort={
                    sort.key === column.key
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <button type="button" onClick={() => onSort(column.key)}>
                    {column.label}
                    {sort.key === column.key && (
                      <span aria-hidden="true">
                        {sort.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((item) => {
              const share = quantityTotal
                ? (item.quantity / quantityTotal) * 100
                : null;

              return (
                <tr key={item.name}>
                  <td>{item.name}</td>
                  <td className="numeric">
                    {formatPizzaQuantity(item.quantity)}
                    {share !== null && (
                      <Share>{share.toFixed(1)}%</Share>
                    )}
                  </td>
                  <td className="numeric">{formatMoney(item.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Section>
  );
};

const PizzaDetailsModal: React.ForwardRefRenderFunction<
  PizzaDetailsModalHandles,
  PizzaDetailsModalProps
> = ({ summary, atacDetails, detailsVersion, onSelectSize }, ref) => {
  const modalRef = useRef<ModalHandles>(null);
  const [visible, setVisible] = useState(false);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [flavorSort, setFlavorSort] = useState<PizzaSort>(DEFAULT_SORT);
  const [complementSort, setComplementSort] = useState<PizzaSort>(DEFAULT_SORT);

  const open = useCallback(() => {
    setFlavorSort(DEFAULT_SORT);
    setComplementSort(DEFAULT_SORT);
    setSelectedSize(summary.sizes[0]?.size ?? null);
    setVisible(true);
    modalRef.current?.open();
  }, [summary.sizes]);

  const close = useCallback(() => {
    setVisible(false);
    modalRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({ open, close }), [open, close]);

  useEffect(() => {
    if (!visible) return;
    const stillThere = summary.sizes.some((size) => size.size === selectedSize);
    if (stillThere) return;
    setSelectedSize(summary.sizes[0]?.size ?? null);
  }, [visible, summary.sizes, selectedSize]);

  useEffect(() => {
    if (!visible || !selectedSize) return;
    onSelectSize(selectedSize);
  }, [visible, selectedSize, detailsVersion, onSelectSize]);

  const selected = summary.sizes.find((size) => size.size === selectedSize);
  const atac = selected
    ? atacDetails[normalizePizzaKey(selected.size)]
    : undefined;
  const details = selected ? resolvePizzaSizeDetails(selected, atac) : null;

  return (
    <Dialog ref={modalRef}>
      <header>
        <div>
          <h3>Pizzas</h3>
          {selected && (
            <p>
              {formatPizzaSize(selected.size)} ·{' '}
              {formatPizzaQuantity(selected.quantity)} ·{' '}
              {formatMoney(selected.total)}
            </p>
          )}
        </div>
        <button type="button" aria-label="Fechar" onClick={close}>
          <FiX />
        </button>
      </header>
      {summary.sizes.length > 0 && (
        <Sizes role="tablist" aria-label="Tamanhos">
          {summary.sizes.map((size) => (
            <SizeTab
              key={size.size}
              type="button"
              role="tab"
              $active={size.size === selectedSize}
              aria-selected={size.size === selectedSize}
              onClick={() => setSelectedSize(size.size)}
            >
              <strong>{formatPizzaSize(size.size)}</strong>
              <span>
                {formatPizzaQuantity(size.quantity)} · {formatMoney(size.total)}
              </span>
            </SizeTab>
          ))}
        </Sizes>
      )}
      {details?.loading ? (
        <LoadingContainer>
          <ActivityIndicator />
        </LoadingContainer>
      ) : (
        <>
          {atac?.status === 'error' && (
            <ErrorText>
              Não foi possível carregar os sabores e complementos deste tamanho.
            </ErrorText>
          )}
          <Tables>
            <StatTable
              label="Sabores"
              items={details?.flavors ?? summary.flavors}
              sort={flavorSort}
              emptyLabel="Nenhum sabor neste tamanho."
              onSort={(key) => setFlavorSort((current) => togglePizzaSort(current, key))}
            />
            <StatTable
              label="Complementos"
              items={details?.complements ?? []}
              sort={complementSort}
              emptyLabel="Nenhum complemento neste tamanho."
              onSort={(key) =>
                setComplementSort((current) => togglePizzaSort(current, key))
              }
            />
          </Tables>
        </>
      )}
      <Footer>
        <button className="default" type="button" onClick={close}>
          <span>Pronto</span>
        </button>
      </Footer>
    </Dialog>
  );
};

export default forwardRef(PizzaDetailsModal);
